import { type FormEvent, memo, useState } from "react";
import { BugIcon, LightbulbIcon } from "lucide-react";

import { cmmdApiClient } from "~/environments/cmmdApi";
import { toastManager } from "../ui/toast";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogPopup,
} from "../ui/dialog";

type FeedbackType = "bug" | "feature";

interface FeedbackDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly senderName?: string;
}

export const FeedbackDialog = memo(function FeedbackDialog({
  open,
  onOpenChange,
  senderName,
}: FeedbackDialogProps) {
  const [type, setType] = useState<FeedbackType>("bug");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  function resetForm() {
    setType("bug");
    setTitle("");
    setDescription("");
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      const typeLabel = type === "bug" ? "Bug Report" : "Feature Suggestion";

      await cmmdApiClient.submitFeedback({
        type,
        title: title.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
        priority: type === "bug" ? "high" : "medium",
        source: "forge",
        ...(senderName ? { senderName } : {}),
      });

      toastManager.add({
        title: type === "bug" ? "Bug reported" : "Suggestion submitted",
        description: `Sent to CMMD feedback intake as a ${typeLabel.toLowerCase()}.`,
        type: "success",
      });
      resetForm();
      onOpenChange(false);
    } catch {
      toastManager.add({
        title: "Could not submit",
        description: "Failed to create CMMD task. Please try again.",
        type: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  const handleOpenChange = (v: boolean) => {
    if (!v) resetForm();
    onOpenChange(v);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPopup showCloseButton>
        <form onSubmit={(e) => void handleSubmit(e)}>
          <DialogHeader>
            <DialogTitle>Send Feedback</DialogTitle>
            <DialogDescription>
              Report a bug or suggest a feature. Your submission goes directly to CMMD.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-4 px-6 py-2">
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setType("bug")}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
                  type === "bug"
                    ? "border-destructive-border bg-destructive-surface text-destructive"
                    : "border-border bg-transparent text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}
              >
                <BugIcon className="size-4 shrink-0" />
                Bug Report
              </button>
              <button
                type="button"
                onClick={() => setType("feature")}
                className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${
                  type === "feature"
                    ? "border-primary/40 bg-primary/8 text-primary"
                    : "border-border bg-transparent text-muted-foreground hover:bg-accent hover:text-foreground"
                }`}
              >
                <LightbulbIcon className="size-4 shrink-0" />
                Suggestion
              </button>
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="feedback-title">
                {type === "bug" ? "What went wrong?" : "What would you like?"}
              </Label>
              <Input
                id="feedback-title"
                placeholder={
                  type === "bug" ? "Short description of the bug" : "Describe the feature"
                }
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                autoFocus
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <Label htmlFor="feedback-description">
                Details{" "}
                <span className="text-xs font-normal text-muted-foreground">(optional)</span>
              </Label>
              <Textarea
                id="feedback-description"
                placeholder={
                  type === "bug"
                    ? "Steps to reproduce, expected vs actual behavior…"
                    : "How would this help you? Any examples?"
                }
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!title.trim() || isSubmitting}>
              {isSubmitting ? "Submitting…" : "Submit"}
            </Button>
          </DialogFooter>
        </form>
      </DialogPopup>
    </Dialog>
  );
});
